import { ChangeEvent, KeyboardEventHandler, MouseEvent as ReactMouseEvent, SyntheticEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import './SecuritySearchModal.css'
import { alpha, Box, Dialog, DialogContent, Divider, IconButton, InputAdornment, Stack, styled, TextField, Typography } from '@mui/material'
import { CancelRounded } from '@mui/icons-material'
import { Security, SecurityCategory, SecurityCategory_CategoryCode, SecurityExpression } from '../../proto/generated/security'
import { exchangeToJSON } from '../../proto/generated/common'
import SecurityCategoryItem from '../securityCategoryItem/SecurityCategoryItem'
import { getCachedSecurityCategories, getCategories, lookupSymbols } from '../../services/apis/securities'
import { selectUserSecurityExpressions } from '../../selectors/security'
import {
    cleanUpBadParenthesis,
    convertDisplayExpressionToExpression,
    convertExpressionToDisplayExpression,
    isSecurityExpressionTerm,
    getHighlightRange,
    getOperandRangeAtSelection,
    getOperatorIndices,
    getOperatorRegex,
    getSearchTermForSelection,
    operandEndingRegex,
    replaceNamesWithExpressions, getSearchExchangeForSelection } from '../../util/expression'
import { ExpressionCallbackData, ExpressionType } from './typings'
import OperatorsPanel from '../operatorsPanel/OperatorsPanel'
import { instrumentLabelsFromCategories, type InstrumentLabels } from '../../util/cashInstrument'
import { getExchangeAndSymbolFromKey, getSecurityAsync, getSecurityFromCache, getSecurityKey, getTypedExchangePrefix } from '../../util/security'
import commonActions from '../../actions/common.actions'
import { selectCurrentTheme, selectMultiPaneFullScreen, selectRecentExpressions } from '../../selectors/common'
import { deleteSecurityExpression } from '../../services/apis/expression'
import SecurityInfiniteScroll from './SecurityInfiniteScroll'
import SecurityInstrumentTypeFilter from './SecurityInstrumentTypeFilter'
import { deleteUserExpressions } from '../../reducer/security.reducer'
import { selectActiveLayoutId, selectPrimarySecurity, selectSecuritySearchOpen } from '../../selectors/home'
import { EXP_FILTER_DELIMTER, MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import SearchIcon from '../../assets/icons/SearchIcon'
import { v4 as uuid } from 'uuid'
import useSecurityExpressionToName from '../../hooks/useExpressionNames'
import ClearIcon from '../../assets/icons/ClearIcon'
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import { ExtraSmallButton } from '../custom'
import { selectLayoutWidgets } from '../../selectors/widgetDashboard'
import { selectActiveChartId } from '../../selectors/chart'
import {
    filterCategoriesForFoUnderlyingSearch,
    isFoUnderlyingSearchContext,
} from '../../util/foUnderlyingSearch'

interface Props {
    open: boolean
    enableExpressions: boolean
    onCloseClicked: () => void
    deleteIcon?: boolean
    onSecuritySelected: Function
    selectedItems?: Set<string>
    tailIcon?: boolean
    watchlistMode?: boolean
    forceCategoryCode?: SecurityCategory_CategoryCode;
    showOnlySimulatorSecurities?: boolean
    showOnlyFOSecurities?: boolean
    compareSecurityPicker?: boolean
}

/** id of the scrolling DialogContent; SecurityInfiniteScroll listens to it for paging. */
export const SEARCH_SCROLL_CONTAINER_ID = 'security-search-scroll'

const SearchDialog = styled(Dialog)(() => ({
    '& .MuiDialog-paper': {
        minHeight: '85vh',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    zIndex: 13000
}))

const SecuritySearchModal = (props: Props) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const darkTheme = useSelector(selectCurrentTheme)
    const {open, onCloseClicked, onSecuritySelected, deleteIcon, tailIcon, selectedItems, enableExpressions, watchlistMode, forceCategoryCode, showOnlySimulatorSecurities = false, showOnlyFOSecurities, compareSecurityPicker} = props
    const securitySearchOpen = useSelector(selectSecuritySearchOpen)
    const { key, expressionMode: expressionEnabled, primary } = useSelector(selectPrimarySecurity)
    const security = expressionEnabled ? null : primary
    const { expressionSecurities } = useSelector(selectPrimarySecurity)
    const globalExpressionToName = useSecurityExpressionToName()
    /**
     * The text the box opens with (current symbol / expression), resolved synchronously when the security is
     * already cached so the first render, and the first search request, carry it. `undefined` means either
     * nothing to prefill or a security that still has to be fetched (see the effect below).
     */
    const [initialValue] = useState<string | undefined>(() => {
        if (securitySearchOpen.key) {
            if (securitySearchOpen.isExpression) {
                const backendKey = key || securitySearchOpen.key
                return globalExpressionToName.get(backendKey) ?? convertExpressionToDisplayExpression(backendKey, expressionSecurities)
            }
            const cached = getSecurityFromCache(securitySearchOpen.key)
            return cached?.symbol ? getSecurityKey(cached.exchange, cached.symbol) : undefined
        }
        if (enableExpressions && expressionEnabled) {
            return globalExpressionToName.get(key) ?? convertExpressionToDisplayExpression(key, expressionSecurities)
        }
        if (security) return `${exchangeToJSON(security.exchange)}:${security.symbol}`
        return undefined
    })
    const initialValuePending = !!securitySearchOpen.key && !securitySearchOpen.isExpression && initialValue === undefined
    const [inputValue, setInputValue] = useState<string>(initialValue ?? '')
    const inputValueRef = useRef(inputValue)
    inputValueRef.current = inputValue
    /** Caret/selection to apply once a programmatic value change has reached the DOM (see setValueAndSelection). */
    const pendingSelection = useRef<{ start: number; end: number } | 'all' | null>(initialValue ? 'all' : null)
    const initialValueApplied = useRef(initialValue !== undefined)
    /** False only while a prefilled security is still being fetched; the list waits so it never searches for ''. */
    const [initialValueResolved, setInitialValueResolved] = useState(!initialValuePending)
    const symbolsUsed = useRef<Record<string, Security>>(
        expressionSecurities.reduce(
            (acc: Record<string, Security>, sec: Security) => {
                acc[`${exchangeToJSON(sec.exchange)}:${sec.symbol}`] = sec
                return acc
            },
            {} as Record<string, Security>
        )
    )
    const cachedSecurities = useRef<Security[]>([])
    const cachedStrategyExpressions = useRef<SecurityExpression[]>([])
    const [selectedCategoryCode, setSelectedCategoryCode] = useState<SecurityCategory_CategoryCode>(SecurityCategory_CategoryCode.ALL)
    const [selectedInstrument, setSelectedInstrument] = useState<string | undefined>(undefined)
    const nameToDisplayExpression = useRef<Map<string, string>>(new Map())
    const displayExpressionToName = useRef<Map<string, string>>(new Map())
    const savedExpressions: SecurityExpression[] | undefined = useSelector(selectUserSecurityExpressions)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const widgets = useSelector(selectLayoutWidgets(activeLayoutId))
    const viewKey = useSelector(selectActiveChartId)
    const currentWidget = useMemo(() => widgets[Number(viewKey?.split('-')[1]) ?? 0], [viewKey, widgets])
    const recentExpressions = useSelector(selectRecentExpressions)
    const foUnderlyingSearch = isFoUnderlyingSearchContext({
        showOnlyFOSecurities,
        watchlistMode,
        widgetType: currentWidget?.widgetType,
    })
    // Categories and instrument labels come from the same response; seeded from the cache so the chips and
    // row labels are there on the first paint instead of arriving a beat after the results.
    const prepareCategories = useCallback(
        (categories: SecurityCategory[]) => (foUnderlyingSearch ? filterCategoriesForFoUnderlyingSearch(categories) : categories),
        [foUnderlyingSearch]
    )
    const [securityCategories, setSecurityCategories] = useState<SecurityCategory[] | undefined>(() => {
        const cached = getCachedSecurityCategories()
        return cached ? prepareCategories(cached.categories) : undefined
    })
    const [instrumentLabels, setInstrumentLabels] = useState<InstrumentLabels>(() => instrumentLabelsFromCategories(getCachedSecurityCategories()))

    const allExpressions = useMemo(() => {
        if (!enableExpressions) {
            return []
        }
        return (savedExpressions?.filter((e) => e.securities?.length > 0 && !e.expired) ?? [])
            .concat(recentExpressions)
            .reduce((unique: SecurityExpression[], e) => {
                if (!unique.some((item) => item.expression === e.expression)) {
                    unique.push(e)
                }
                return unique
            }, [])
            .map((e) => {
                const displayExpression = convertExpressionToDisplayExpression(e.expression, e.securities)
                if (e.name) {
                    nameToDisplayExpression.current.set(e.name, displayExpression)
                    displayExpressionToName.current.set(displayExpression, e.name)
                }
                return {
                    id: e.id ?? e.expression,
                    name: e.name ?? convertExpressionToDisplayExpression(e.expression, e.securities),
                    expression: e.expression,
                    securities: e.securities,
                    expired: false
                }
            })
    }, [savedExpressions, recentExpressions])

    /** Search term and typed exchange prefix for the operand under the caret (whole text when expressions are off). */
    const searchTermForSelection = useCallback(
        (rawValue: string, start: number, end: number): { term: string; exchange: string | undefined } => {
            const value = rawValue.toUpperCase()
            if (!enableExpressions) {
                const [, symbol] = getExchangeAndSymbolFromKey(value)
                return { term: symbol.trim(), exchange: getTypedExchangePrefix(value) }
            }
            return {
                term: getSearchTermForSelection(value, start, end, nameToDisplayExpression.current),
                exchange: getSearchExchangeForSelection(value, start, end)
            }
        },
        [enableExpressions]
    )
    const [searchTerm, setSearchTerm] = useState<string>(() => (initialValue ? searchTermForSelection(initialValue, 0, initialValue.length).term : ''))
    /** Exchange typed as a prefix (`BSE:RELIANCE`), sent with the search so that listing leads the results. */
    const [searchExchange, setSearchExchange] = useState<string | undefined>(() =>
        initialValue ? searchTermForSelection(initialValue, 0, initialValue.length).exchange : undefined
    )
    const [focusedIndex, setFocusedIndex] = useState<number>(NaN)
    const focusedIndexRef = useRef(focusedIndex)
    focusedIndexRef.current = focusedIndex
    const maxItemsFocusable = useRef<number>(0)
    const comparePickerSubmitRef = useRef<(() => void) | null>(null)

    // A saved strategy name or any operator switches the box into expression (spread) editing.
    // `allExpressions` (re)populates nameToDisplayExpression while rendering, hence the dependency.
    const expressionMode = useMemo(() => {
        if (!enableExpressions) return false
        return allExpressions.some((exp) => exp.name === inputValue) || nameToDisplayExpression.current.has(inputValue) || getOperatorRegex().test(inputValue)
    }, [enableExpressions, inputValue, allExpressions])

    /**
     * Filter the list by the operand under the caret (or by the whole text when expressions are disabled).
     * Skipped while a row is keyboard-focused: its symbol is only being previewed in the box.
     */
    const syncSearchTermToSelection = useCallback(
        (input: HTMLInputElement) => {
            if (!Number.isNaN(focusedIndexRef.current)) return
            const start = input.selectionStart ?? input.value.length
            const end = input.selectionEnd ?? start
            const { term, exchange } = searchTermForSelection(input.value, start, end)
            setSearchTerm(term)
            setSearchExchange(exchange)
        },
        [searchTermForSelection]
    )

    const applyPendingSelection = useCallback(() => {
        const input = inputRef.current
        const pending = pendingSelection.current
        if (!input || !pending) return
        pendingSelection.current = null
        input.focus()
        if (pending === 'all') {
            input.select()
        } else {
            const length = input.value.length
            input.setSelectionRange(Math.min(pending.start, length), Math.min(pending.end, length))
        }
        syncSearchTermToSelection(input)
    }, [syncSearchTermToSelection])

    /**
     * Set the box's text together with where the caret (or selection) must land. React rewrites the DOM value
     * on commit, which throws the caret to the end, so the selection is applied in a layout effect right after.
     */
    const setValueAndSelection = useCallback(
        (nextValue: string, selection: { start: number; end: number } | 'all') => {
            pendingSelection.current = selection
            if (nextValue === inputValueRef.current) {
                // Nothing to commit. Defer past React's own controlled-value restore (which follows the event handler).
                queueMicrotask(applyPendingSelection)
                return
            }
            setInputValue(nextValue)
        },
        [applyPendingSelection]
    )

    // Runs right after each committed value change, before paint, so the caret never flashes at the end.
    useLayoutEffect(() => {
        applyPendingSelection()
    }, [inputValue, applyPendingSelection])

    /** Arrow-key navigation previews the focused row's symbol in the box, caret at the end. */
    const previewInputValue = useCallback(
        (value: string) => {
            setValueAndSelection(value, { start: value.length, end: value.length })
        },
        [setValueAndSelection]
    )

    const clearInput = useCallback(() => {
        setFocusedIndex(NaN)
        setValueAndSelection('', { start: 0, end: 0 })
    }, [setValueAndSelection])

    // Clicking the bar's padding, icon or divider focuses the field without starting a dialog drag
    // (the bar sits inside the title, which is react-draggable's handle) and without blurring the input.
    const onSearchBarMouseDown = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement
        if (target.closest('input, button')) return
        event.preventDefault()
        event.stopPropagation()
        inputRef.current?.focus()
    }, [])

    const dispatch = useDispatch()

    const placeholder = useMemo(() => {
        if (showOnlySimulatorSecurities) return 'Choose an index from the list below'
        if (foUnderlyingSearch) return 'e.g. NIFTY or RELIANCE'
        if (compareSecurityPicker) return 'e.g. BANKNIFTY FUT or TCS'
        if (watchlistMode) return 'e.g. NIFTY 23000 CE or TCS FUT'
        if (enableExpressions) return 'e.g. NIFTY 23000 CE, TCS FUT or =NSE:HDFCBANK+NSE:INFY'
        return 'e.g. NIFTY 23000 CE or TCS FUT'
    }, [showOnlySimulatorSecurities, foUnderlyingSearch, compareSecurityPicker, watchlistMode, enableExpressions])

    useEffect(() => {
        if (!open) {
            return
        }
        let cancelled = false
        getCategories()
            .then((securityCategoriesResponse) => {
                if (cancelled) return
                setInstrumentLabels(instrumentLabelsFromCategories(securityCategoriesResponse))
                // Select-underlying: All / Stocks / Index only (cash underlyings with hasFo).
                setSecurityCategories(prepareCategories(securityCategoriesResponse.categories))
                if (foUnderlyingSearch) {
                    setSelectedCategoryCode(SecurityCategory_CategoryCode.ALL)
                    setSelectedInstrument(undefined)
                }
            })
            .catch((error) => {
                console.error('Error fetching security categories', error)
                // Still show results (without chips) rather than an empty dialog.
                if (!cancelled) setSecurityCategories((previous) => previous ?? [])
            })
        return () => {
            cancelled = true
        }
    }, [open, foUnderlyingSearch, prepareCategories])

    function onCategorySelected(code: SecurityCategory_CategoryCode) {
        if (forceCategoryCode) return;
        setSelectedCategoryCode(code)
        setSelectedInstrument(undefined)
    }

    const selectedCategory = useMemo(
        () => (securityCategories ?? []).find((category) => category.code === selectedCategoryCode),
        [securityCategories, selectedCategoryCode]
    )
    // Charts: type filter when 2+ subtypes. FO underlying picker: no subtypes (TV-style).
    const categorySubtypes = useMemo(() => {
        if (foUnderlyingSearch) {
            return []
        }
        if (
            selectedCategoryCode === SecurityCategory_CategoryCode.ALL ||
            selectedCategoryCode === SecurityCategory_CategoryCode.SPREADS
        ) {
            return []
        }
        const subtypes = selectedCategory?.subtypes ?? []
        return subtypes.length > 1 ? subtypes : []
    }, [foUnderlyingSearch, selectedCategory, selectedCategoryCode])

    // Prefill with a security that was not cached at mount (everything else is seeded in the initial state):
    // fetched once, then shown fully selected so typing replaces it. The term is set in the same batch as the
    // text so the list's first request already carries it.
    useEffect(() => {
        if (initialValueApplied.current) return
        initialValueApplied.current = true
        const key = securitySearchOpen.key
        if (!key) {
            setInitialValueResolved(true)
            return
        }
        let cancelled = false
        getSecurityAsync(key)
            .then((security) => {
                if (cancelled) return
                if (security?.symbol && inputValueRef.current.length === 0) {
                    const value = getSecurityKey(security.exchange, security.symbol)
                    const { term, exchange } = searchTermForSelection(value, 0, value.length)
                    setValueAndSelection(value, 'all')
                    setSearchTerm(term)
                    setSearchExchange(exchange)
                }
                setInitialValueResolved(true)
            })
            .catch(() => {
                if (!cancelled) setInitialValueResolved(true)
            })
        return () => {
            cancelled = true
        }
    }, [securitySearchOpen.key, searchTermForSelection, setValueAndSelection])

    const onInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const input = event.target
            const raw = input.value
            // Leave text alone mid-IME-composition; the composed result arrives in a later change event.
            const value = (event.nativeEvent as InputEvent).isComposing ? raw : raw.toUpperCase()
            // Keep the caret where the browser put it. Positions go through toUpperCase too, as it can change length (ß → SS).
            const caretStart = raw.slice(0, input.selectionStart ?? raw.length).toUpperCase().length
            const caretEnd = raw.slice(0, input.selectionEnd ?? input.selectionStart ?? raw.length).toUpperCase().length
            setFocusedIndex(NaN)
            setValueAndSelection(value, { start: caretStart, end: caretEnd })
        },
        [setValueAndSelection]
    )

    // Bound to onSelect and onClick: the click covers a caret placed by collapsing an existing selection.
    const onInputSelect = useCallback(
        (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            syncSearchTermToSelection(event.currentTarget as HTMLInputElement)
        },
        [syncSearchTermToSelection]
    )

    // The Dialog's portal mounts the field a render after the mount-time layout effect, which therefore found
    // no input and left the initial "select all" pending. autoFocus fires once the field exists, so a pending
    // selection (the prefilled symbol) is applied here; later focuses find nothing pending and do nothing.
    const onInputFocus = useCallback(() => {
        if (pendingSelection.current) queueMicrotask(applyPendingSelection)
    }, [applyPendingSelection])

    // React's onSelect misses a click that collapses an existing selection (mouseup still reports the old
    // range; the caret arrives in a later selectionchange). Track the document's selectionchange directly.
    useEffect(() => {
        if (!open) return
        const onSelectionChange = () => {
            const input = inputRef.current
            if (input && document.activeElement === input) syncSearchTermToSelection(input)
        }
        document.addEventListener('selectionchange', onSelectionChange)
        return () => document.removeEventListener('selectionchange', onSelectionChange)
    }, [open, syncSearchTermToSelection])

    const onExpressionSelected = useCallback(
        (expression: SecurityExpression, selected: boolean, securities: Security[], type: ExpressionType) => {
            const securityExpression = allExpressions.find((exp) => exp.expression === expression?.expression)
            if (!securities && !securityExpression) {
                commonActions.setApiError(dispatch, { error: true, message: 'Something went wrong', errorType: 'error' })
                return
            }
            onSecuritySelected(
                {
                    expression: cleanUpBadParenthesis(expression.expression, false),
                    securities: securities ?? securityExpression?.securities,
                    expressionMode: true,
                    expressionId: expression.id ?? cleanUpBadParenthesis(expression.expression, false),
                    type: type,
                    name: expression.name
                },
                selected
            )
        },
        [onSecuritySelected, allExpressions, dispatch]
    )

    const onExpressionDeleted = useCallback(
        (expression: string) => {
            const securityExpression = (savedExpressions ?? []).find((exp) => exp.expression === expression)
            commonActions.deleteRecentExpression(dispatch, expression)
            if (securityExpression) {
                deleteSecurityExpression({ expression: securityExpression }).then(() => {
                    dispatch(deleteUserExpressions(securityExpression))
                })
            }
        },
        [savedExpressions, dispatch]
    )

    const onSecuritySelect = useCallback(
        (data: Security | SecurityExpression, selected: boolean, focused: boolean, type: ExpressionType | 'SECURITY') => {
            const isExpression = ['EXPRESSION', 'STRATEGY_EXPRESSION'].includes(type)
            if (!expressionMode || (expressionMode && isExpression)) {
                if (isExpression) {
                    onExpressionSelected(
                        focused
                            ? {
                                  ...(data as SecurityExpression),
                                  expression: cleanUpBadParenthesis((data as SecurityExpression).expression)
                              }
                            : (data as SecurityExpression),
                        selected,
                        (data as SecurityExpression).securities,
                        type as ExpressionType
                    )
                } else {
                    onSecuritySelected({ expression: '', securities: [data], expressionMode: false }, selected)
                }
                return
            }
            // A picked security replaces the whole operand(s) under the caret or selection (a partial
            // selection such as `NIFTY` inside `NSE:NIFTY` still swaps the full `NSE:NIFTY`); a picked
            // expression replaces the entire input.
            const input = inputRef.current
            let start = 0
            let end = inputValue.length
            if (!isExpression) {
                const selectionStart = input?.selectionStart ?? inputValue.length
                const selectionEnd = input?.selectionEnd ?? selectionStart
                ;[start, end] = getOperandRangeAtSelection(inputValue, selectionStart, selectionEnd)
            }

            let term
            if (isExpression) {
                const displayExpression = convertExpressionToDisplayExpression((data as SecurityExpression).expression, (data as SecurityExpression).securities)
                term = displayExpressionToName.current.get(displayExpression) ?? displayExpression
                ;(data as SecurityExpression).securities?.forEach((sec) => {
                    symbolsUsed.current[(exchangeToJSON(sec.exchange) || 'NSE') + ':' + sec.symbol] = sec
                })
            } else {
                term = (exchangeToJSON((data as Security).exchange) || 'NSE') + ':' + (data as Security).symbol
                symbolsUsed.current[term] = data as Security
            }
            const caret = start + term.length
            setValueAndSelection(inputValue.substring(0, start) + term + inputValue.substring(end), { start: caret, end: caret })
        },
        [expressionMode, inputValue, onExpressionSelected, onSecuritySelected, setValueAndSelection]
    )

    // Operators apply to the top-level operand under the caret; a selected sub-expression is parenthesised first.
    const onOperatorClick = useCallback(
        (value: string) => {
            const input = inputRef.current
            const selectionStart = input?.selectionStart ?? inputValue.length
            const selectionEnd = input?.selectionEnd ?? selectionStart
            const [start, end] = getHighlightRange(inputValue, selectionStart, selectionEnd)
            let operand = inputValue.substring(start, end)
            if (getOperatorRegex().test(operand)) {
                operand = cleanUpBadParenthesis(operand, true)
            }
            operand = value === '1/' ? `1/${operand}` : operand + value
            const caret = start + operand.length
            setValueAndSelection(inputValue.substring(0, start) + operand + inputValue.substring(end), { start: caret, end: caret })
        },
        [inputValue, setValueAndSelection]
    )

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
        async (e) => {
            if (e.key === 'ArrowDown') {
                if (Number.isNaN(focusedIndex)) {
                    setFocusedIndex(0)
                } else if (focusedIndex + 1 <= maxItemsFocusable.current - 1) {
                    setFocusedIndex(focusedIndex + 1)
                }
                e.preventDefault()
            } else if (e.key === 'ArrowUp') {
                if (Number.isNaN(focusedIndex)) {
                    setFocusedIndex(0)
                } else if (focusedIndex === 0) {
                    setFocusedIndex(NaN)
                } else {
                    setFocusedIndex(focusedIndex - 1)
                }
                e.preventDefault()
            }
        },
        [focusedIndex]
    )

    const onKeyUp: KeyboardEventHandler<HTMLInputElement> = useCallback(
        async (e) => {
            if (e.key === 'Enter') {
                if (compareSecurityPicker && !expressionMode && !Number.isNaN(focusedIndex)) {
                    comparePickerSubmitRef.current?.()
                    e.preventDefault()
                    return
                }
                const rawInputValue = replaceNamesWithExpressions(inputValue, nameToDisplayExpression.current)
                if (operandEndingRegex.test(rawInputValue)) {
                    return
                }
                const indices = getOperatorIndices(rawInputValue)
                const securitiesToFetch = new Set<string>()
                const invalidSymbols = new Set<string>()
                const securities: Security[] = []
                indices.forEach(([start, end]) => {
                    if (end > start) {
                        const term = rawInputValue.substring(start, end + 1).trim()
                        if (isSecurityExpressionTerm(term)) {
                            let [code] = term.trim().split(EXP_FILTER_DELIMTER)
                            code = code.trim()
                            const sec = symbolsUsed.current[code]

                            if (!sec || sec.categoryCode === undefined) {
                                if (term.indexOf(':') === -1) {
                                    invalidSymbols.add(code)
                                } else {
                                    securitiesToFetch.add(code)
                                }
                            } else {
                                securities.push(sec)
                            }
                        }
                    }
                })
                if (invalidSymbols.size > 0) {
                    return
                }
                if (securitiesToFetch.size > 0) {
                    try {
                        const response = await lookupSymbols(
                            Array.from(securitiesToFetch).map((sec) => {
                                const [exchange, symbol] = getExchangeAndSymbolFromKey(sec)
                                return {
                                    exchange,
                                    symbol
                                }
                            })
                        )
                        if (response && response.securities.length) {
                            response.securities.forEach((sec) => {
                                symbolsUsed.current[`${exchangeToJSON(sec.exchange)}:${sec.symbol}`] = sec
                                securities.push(sec)
                            })
                        }
                    } catch (e) {
                        console.error(e)
                    }
                }
                let finalExpression = convertDisplayExpressionToExpression(rawInputValue, securities)

                if (selectedItems && selectedItems?.has(finalExpression)) {
                    commonActions.setApiError(dispatch, {
                        error: true,
                        message: 'Expression already added',
                        errorType: 'error'
                    })
                    return
                }
                const data: ExpressionCallbackData = {
                    expression: cleanUpBadParenthesis(finalExpression, false),
                    expressionId: uuid(),
                    securities: securities,
                    expressionMode: true,
                    type: 'EXPRESSION'
                }
                if (securities.length === 1 && finalExpression === getSecurityKey(securities[0].exchange, securities[0].code)) {
                    data.expression = ''
                    data.expressionMode = false
                    data.type = undefined
                } else if (displayExpressionToName.current.has(rawInputValue)) {
                    data.name = displayExpressionToName.current.get(rawInputValue)!
                    if (!new Set(allExpressions.map((exp) => exp.name)).has(data.name)) {
                        data.type = 'STRATEGY_EXPRESSION'
                    }
                }
                onSecuritySelected(data, true)
            }
        },
        [inputValue, selectedItems, onSecuritySelected, dispatch, allExpressions, compareSecurityPicker, expressionMode, focusedIndex]
    )

    const onMaxFocusableChange = useCallback((max: number) => {
        maxItemsFocusable.current = max
    }, [])

    const reduxContainer = useSelector(selectMultiPaneFullScreen)
    // Use native fullscreen element (covers option-chain fullscreen) OR fall back to Redux multipane fullscreen
    const nativeFullscreenEl = useSyncExternalStore(
        (cb) => { document.addEventListener('fullscreenchange', cb); return () => document.removeEventListener('fullscreenchange', cb) },
        () => document.fullscreenElement as HTMLElement | null,
        () => null
    )
    const container = nativeFullscreenEl ?? (reduxContainer ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null)

    return (
        <SearchDialog
            open={open}
            onClose={onCloseClicked}
            aria-labelledby="customized-dialog-title"
            maxWidth={'md'}
            container={container ?? undefined}
            fullWidth
        >
            <DraggableDialogTitle id="customized-dialog-title">
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                    <Typography variant={'h6'}>{foUnderlyingSearch ? 'Underlying Search' : 'Symbol Search'}</Typography>
                    <ExtraSmallButton onClick={onCloseClicked} aria-label="close" color={'inherit'}>
                        <ClearIcon fontSize={'small'} />
                    </ExtraSmallButton>
                </Box>
                <Box
                    onMouseDown={onSearchBarMouseDown}
                    sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        mt: 1.5,
                        mb: 0.5,
                        pl: 1.5,
                        pr: 1,
                        minHeight: 44,
                        borderRadius: '6px',
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
                        transition: theme.transitions.create('border-color', { duration: theme.transitions.duration.shorter }),
                        '&:focus-within': { borderColor: alpha(theme.palette.text.primary, 0.32) },
                        // The dialog title around this bar is the drag handle (grab cursor, no text selection);
                        // the bar itself must still look and behave like a text field.
                        cursor: 'text',
                        userSelect: 'text',
                        '& .MuiInputBase-root': { cursor: 'text' },
                        '& .MuiInputBase-input': { cursor: 'text', userSelect: 'text', py: 0.5 }
                    })}
                >
                    <SearchIcon sx={{ fontSize: 22, opacity: 0.5, mr: 1, flexShrink: 0 }} />
                    <TextField
                        variant="standard"
                        fullWidth
                        inputRef={inputRef}
                        multiline={false}
                        placeholder={placeholder}
                        value={inputValue}
                        disabled={showOnlySimulatorSecurities}
                        InputProps={{
                            disableUnderline: true,
                            endAdornment: inputValue ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="Clear search"
                                        onClick={clearInput}
                                        sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'text.primary', backgroundColor: 'transparent' } }}
                                    >
                                        <CancelRounded sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined
                        }}
                        inputProps={{
                            onSelect: onInputSelect,
                            onClick: onInputSelect,
                            onFocus: onInputFocus,
                            onKeyDown,
                            onKeyUp,
                            spellCheck: false,
                            autoCapitalize: 'characters'
                        }}
                        sx={{ flex: 1, minWidth: 0 }}
                        onChange={onInputChange}
                        autoFocus
                        autoComplete="off"
                    />
                    {(enableExpressions && !showOnlySimulatorSecurities) && (
                        <>
                            <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1 }} />
                            <OperatorsPanel onClick={(value) => onOperatorClick(value)} enabled={true} />
                        </>
                    )}
                </Box>
            </DraggableDialogTitle>
            {!forceCategoryCode && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        gap: 1,
                        px: 3,
                        pb: 1.5,
                        pt: 0,
                        minHeight: 44,
                        flexShrink: 0,
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        flexWrap="nowrap"
                        useFlexGap
                        spacing={0.5}
                        sx={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            overflowX: 'auto',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                        }}
                    >
                        {(securityCategories ?? []).map((category) => (
                                <SecurityCategoryItem
                                    key={category.code}
                                    onSelected={onCategorySelected}
                                    selected={selectedCategoryCode === category.code}
                                    category={category}
                                    darkTheme={darkTheme}
                                    inputRef={inputRef}
                                />
                            ))}
                    </Stack>
                    {!foUnderlyingSearch && (
                        <SecurityInstrumentTypeFilter
                            subtypes={categorySubtypes}
                            selectedInstrument={selectedInstrument}
                            menuContainer={container}
                            menuZIndex={14000}
                            onChange={(instrument) => {
                                setSelectedInstrument(instrument)
                                inputRef.current?.focus()
                            }}
                        />
                    )}
                </Box>
            )}
            {/* the DialogContent is the scroll container; the list below targets it via scrollableTarget */}
            <DialogContent id={SEARCH_SCROLL_CONTAINER_ID} sx={{ padding: 0, flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
                {/* Mount the list only once the search term and the instrument labels are known: an earlier mount
                    would search for '' and paint fallback labels, both replaced a moment later (visible flicker). */}
                {securityCategories !== undefined && (initialValueResolved || inputValue.length > 0) && (
                <SecurityInfiniteScroll
                    categoryCode={forceCategoryCode ?? selectedCategoryCode}
                    instrument={categorySubtypes.length > 0 ? selectedInstrument : undefined}
                    searchText={searchTerm || ''}
                    exchange={searchExchange}
                    instrumentLabels={instrumentLabels}
                    tailIcon={tailIcon}
                    deleteIcon={deleteIcon}
                    expressions={allExpressions}
                    cachedSecurities={cachedSecurities}
                    cachedStrategyExpressions={cachedStrategyExpressions}
                    highlightSelected={watchlistMode || false}
                    expressionToName={displayExpressionToName}
                    nameToExpression={nameToDisplayExpression}
                    onSecuritySelect={onSecuritySelect}
                    onExpressionDeleted={onExpressionDeleted}
                    selectedItems={selectedItems}
                    setInputValue={previewInputValue}
                    focusedIndex={focusedIndex}
                    onMaxFocusableChange={onMaxFocusableChange}
                    expressionMode={expressionMode}
                    showOnlySimulatorSecurities={showOnlySimulatorSecurities}
                    showOnlyFOSecurities={foUnderlyingSearch}
                    watchlistMode={watchlistMode}
                    compareSecurityPicker={compareSecurityPicker}
                    comparePickerSubmitRef={comparePickerSubmitRef}
                    onCompareSecurityPick={
                        compareSecurityPicker
                            ? (security, placement) => {
                                  onSecuritySelected(
                                      {
                                          expression: '',
                                          securities: [security],
                                          expressionMode: false,
                                          comparePlacement: placement
                                      },
                                      true
                                  )
                              }
                            : undefined
                    }
                />
                )}
            </DialogContent>
        </SearchDialog>
    )
}

export default SecuritySearchModal
